import React, { useState } from 'react';
import { ArrowRight, Users, Code, Palette, Shield, BarChart3, Search, Smartphone, Settings, TestTube, Package, Pencil } from 'lucide-react';
import { roleRegistry } from '../../data/roles/index';

const RolesPage = ({ onRoleSelect }) => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  // Role icons mapping
  const roleIcons = {
    'ux-designer': Palette,
    'ui-designer': Palette,
    'product-designer': Palette,
    'content-designer': Pencil,
    'ux-researcher': Search,
    'frontend': Code,
    'backend': Code,
    'fullstack': Code,
    'mobile': Smartphone,
    'devops': Settings,
    'qa': TestTube,
    'product-manager': BarChart3,
    'product-owner': Package
  };

  // Department categories
  const departments = {
    all: 'All Roles',
    design: 'Design',
    engineering: 'Engineering',
    product: 'Product'
  };

  // Get roles by department
  const getRolesByDept = (dept) => {
    if (dept === 'all') return Object.keys(roleRegistry);
    
    return Object.entries(roleRegistry)
      .filter(([, role]) => role.config?.department === dept)
      .map(([roleId]) => roleId);
  };

  const filteredRoles = getRolesByDept(selectedDepartment);

  const handleRoleClick = (roleId) => {
    const roleConfig = roleRegistry[roleId]?.config;
    if (roleConfig && onRoleSelect) {
      onRoleSelect({
        role: roleId,
        experience: 'junior',
        os: 'macos',
        name: ''
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="p-3 bg-blue-600 rounded-2xl">
              <Users className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome to Your Onboarding Journey
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose your role to get personalized, step-by-step guidance tailored to your experience level and operating system.
          </p>
        </div>

        {/* Department Filter */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-xl p-2 shadow-sm border border-gray-200">
            <div className="flex space-x-1">
              {Object.entries(departments).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedDepartment(key)}
                  className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                    selectedDepartment === key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredRoles.map((roleId) => {
            const role = roleRegistry[roleId];
            const config = role?.config;
            const IconComponent = roleIcons[roleId] || Code;
            
            if (!config) return null;

            return (
              <div
                key={roleId}
                onClick={() => handleRoleClick(roleId)}
                className="group relative bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
              >
                {/* Role Icon */}
                <div className={`inline-flex p-3 rounded-xl mb-4 bg-${config.color}-100 group-hover:bg-${config.color}-200 transition-colors duration-300`}>
                  <IconComponent className={`w-6 h-6 text-${config.color}-600`} />
                </div>

                {/* Role Info */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                  {config.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {config.description}
                </p>

                {/* Department Badge */}
                <div className="flex items-center justify-between">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium bg-${config.color}-50 text-${config.color}-700`}>
                    {config.department}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" />
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-indigo-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            );
          })}
        </div>

        {/* Footer Info */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200 max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Personalized Onboarding Experience
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-medium text-gray-900 mb-1">Experience-Adapted</h3>
                <p>Junior, Mid-level, or Senior guidance</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-3">
                  <Settings className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-medium text-gray-900 mb-1">OS-Specific</h3>
                <p>macOS, Windows, and Linux instructions</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
                  <Package className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-medium text-gray-900 mb-1">Comprehensive</h3>
                <p>9 essential onboarding sections</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPage;
